import React, { useState } from 'react';
import { Box, Button, Popover } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { FavoriteDropdownList, FavoriteDropdownTestIds } from './favorite-dropdown-list';

export interface FavoriteDropdownProps<T> {
  items: T[];
  selectedItemId: string | undefined;
  favoriteItemId: string | undefined;
  onSelectItem: (item: T) => void;
  onToggleFavorite: (item: T) => void;
  getItemId: (item: T) => string | undefined;
  getItemLabel: (item: T) => string;
  label?: string;
  minWidth?: number;
  disableWhenSingle?: boolean;
  disabled?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  sortItems?: (a: T, b: T) => number;
  testIds?: FavoriteDropdownTestIds;
}

export function FavoriteDropdown<T>(props: FavoriteDropdownProps<T>) {
  const {
    items,
    selectedItemId,
    favoriteItemId,
    onSelectItem,
    onToggleFavorite,
    getItemId,
    getItemLabel,
    label = 'Vælg',
    minWidth = 350,
    disableWhenSingle = true,
    disabled,
    onOpen,
    onClose,
    sortItems,
    testIds,
  } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    onOpen?.();
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
    onClose?.();
  }

  function handleSelectItem(item: T) {
    onSelectItem(item);
    handleClose();
  }

  const open = Boolean(anchorEl);
  const selectedItem = items.find(item => getItemId(item) === selectedItemId);
  const displayText = selectedItem ? getItemLabel(selectedItem) : label;
  const isDisabled = Boolean(disabled || (disableWhenSingle && items.length < 2));

  return (
    <Box sx={{ minWidth }}>
      <Button
        onClick={handleClick}
        endIcon={<KeyboardArrowDown />}
        variant="outlined"
        sx={{
          minWidth,
          width: '100%',
          justifyContent: 'space-between',
          textAlign: 'left',
          textTransform: 'none',
          backgroundColor: 'white',
          '&:hover': {
            backgroundColor: 'white',
          },
        }}
        disabled={isDisabled}
      >
        {displayText}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <FavoriteDropdownList
          items={items}
          favoriteItemId={favoriteItemId}
          selectedItemId={selectedItemId}
          onSelectItem={handleSelectItem}
          onToggleFavorite={onToggleFavorite}
          getItemId={getItemId}
          getItemLabel={getItemLabel}
          minWidth={minWidth}
          sortItems={sortItems}
          testIds={testIds}
        />
      </Popover>
    </Box>
  );
}
